import { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { ErrorHandler } from "@ploy-lib/core";
import {
	ErrorPage,
	ImageFit,
	ThirdPartyScripts,
	SignedOnUser,
	GlobalSuspense,
	ThirdPartyScriptsContext,
	useThirdPartyScriptsContext
} from "@ploy-ui/core";
import { Theme } from "@material-ui/core";

import {
	ApplicationView,
	ProductView,
	ProductViewProps,
	RouteNavigationProvider
} from "@ploy-ui/form-views";
import { useResource, useInvalidator } from "@rest-hooks/core";
import {
	PartnerWebShortRefResource,
	LoginResource,
	ProductSettingsResource
} from "@ploy-lib/rest-resources";
import { useTenantImage, useTenant } from "@ploy-ui/tenants";
import { BootstrapQueryEvents } from "./AuthenticatedAgent";
import { SnackbarProvider } from "notistack";
import clsx from "clsx";
import { Helmet } from "react-helmet";

const useStyles = makeStyles(
	(theme: Theme) => ({
		root: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			flexDirection: "column"
		},
		page: {
			maxWidth: 1440,
			width: "100%",
			overflow: "hidden"
		},
		logoWrapper: {
			paddingTop: theme.spacing(4),
			paddingBottom: theme.spacing(1),
			[theme.breakpoints.down("sm")]: {
				paddingTop: theme.spacing(1)
			}
		},
		logo: {
			width: "100%",
			height: 64
		},
		signedIn: {
			paddingRight: theme.spacing(4)
		}
	}),
	{ name: "DployFormViews" }
);
// Disallow the field role navigate_home as it doesn'ẗ make sense in snippet context
const disallowedFieldRoles = ["navigate_home"] as const;

const ApplicationByShortRef = () => {
	const { shortRef } = useParams<"shortRef">();
	const { applicationNumber } = useResource(
		PartnerWebShortRefResource.detail(),
		{ shortRef }
	);

	const loginStatus = useInvalidator(LoginResource.status());

	useEffect(() => {
		loginStatus({});
	}, [loginStatus, shortRef]);

	return (
		<ApplicationView
			applicationNumber={applicationNumber!}
			disallowedFieldRoles={disallowedFieldRoles}
		/>
	);
};

const GetProductRoutes = (basepath: string, props: ProductViewProps) => {
	const { isAuthenticated } = useResource(LoginResource.status(), {});
	const productSettings = useResource(
		ProductSettingsResource.detail(),
		isAuthenticated ? {} : null
	);

	if (productSettings == null) return null;

	return (
		<Route path={basepath}>
			<Route
				path=":externalCode/*"
				element={<ProductView hideHeader {...props} />}
			/>
			<Route index element={<ProductView hideHeader {...props} />} />
		</Route>
	);
};

export const App = () => {
	const classes = useStyles();

	const logo = useTenantImage("form_web_logo");
	const { brandAdditions } = useTenant();
	let useLogo = !(brandAdditions?.formWebLogoDisabled ?? false);

	return (
		<>
			<Helmet
				htmlAttributes={{
					lang: useTenant().locale,
					translate: window.env?.REACT_APP_ENABLE_TRANSLATION as
						| "yes"
						| "no"
						| undefined
				}}
			/>
			<SnackbarProvider maxSnack={3}>
				<GlobalSuspense>
					{useLogo && logo && (
						<div className={clsx(classes.logoWrapper, classes.root)}>
							<ImageFit
								className={classes.logo}
								src={logo}
								alt="logo"
								fit="contain"
							/>
						</div>
					)}
					<SignedOnUser align="right" className={classes.signedIn} />
					<div className={classes.root}>
						<div className={classes.page}>
							<ThirdPartyScriptsContext.Provider
								value={useThirdPartyScriptsContext()}
							>
								<ErrorHandler
									fallback={(error: any) => <ErrorPage error={error} />}
								>
									<Routes>
										<Route
											element={
												<RouteNavigationProvider application="/application/:applicationNumber">
													<Outlet />
												</RouteNavigationProvider>
											}
										>
											<Route path="ref">
												<Route
													element={<ApplicationByShortRef />}
													path=":shortRef/*"
												/>
											</Route>

											<Route path="application">
												<Route
													path=":applicationNumber/*"
													element={
														<ApplicationView
															disallowedFieldRoles={disallowedFieldRoles}
														/>
													}
												/>
											</Route>

											<Route
												element={
													<BootstrapQueryEvents>
														<Outlet />
													</BootstrapQueryEvents>
												}
											>
												{GetProductRoutes("product", {
													enableSelect: false,
													disallowedFieldRoles
												})}

												{GetProductRoutes("all", {
													enableSelect: true,
													disallowedFieldRoles
												})}

												{GetProductRoutes("calculator", {
													formContext: "Calculator",
													enableSelect: true,
													disallowedFieldRoles
												})}

												{GetProductRoutes(":category", {
													enableSelect: true,
													disallowedFieldRoles
												})}

												<Route index element={<Navigate to="all" replace />} />
											</Route>
										</Route>
									</Routes>
								</ErrorHandler>
								<ThirdPartyScripts />
							</ThirdPartyScriptsContext.Provider>
						</div>
					</div>
				</GlobalSuspense>
			</SnackbarProvider>
		</>
	);
};
